// FUNC-131 — Matriz de precios de sesión con dimensión "método de pago".
//
// Espejo del CASE server-side de `create_consultation` (rama pública) y
// `create_consultation_investor`. ÚNICA fuente de precio del lado `src/`: la
// consumen el wizard (paciente nuevo y existente) y "Nueva Cita" del portal.
// Si se toca un precio, se toca acá Y en la migración SQL (docs/PRECIOS.md).
//
// Regla de negocio (2026-10-06):
//  - Presencial: pagar con transferencia/efectivo tiene valor preferente;
//    débito/crédito paga el precio de lista.
//  - Online: mismo precio con cualquier método (matriz de lista + promo FUNC-105).
//  - Domicilio: pareja 90 min, $75.000 plano con cualquier método.
//  - Familia: deshabilitada — ya no se puede crear, no hay precio.
//  - `tarifa_diferenciada` del paciente manda por encima de todo: la aplica el
//    caller sobre el resultado de esta función, el método no la modifica.

import { precioListaConPromo } from './promoOnline';

/** Método declarado al agendar. 'tarjeta' = débito/crédito (precio de lista). */
export type MetodoPagoDeclarado = 'efectivo' | 'transferencia' | 'tarjeta';

export const PRECIO_DOMICILIO = 75000;

type TipoPrecio = 'individual' | 'pareja';

// Precio de lista (débito/crédito, y online con cualquier método).
const PRECIO_LISTA: Record<TipoPrecio, Record<number, number>> = {
  individual: { 45: 36000, 90: 41000 },
  pareja: { 50: 38750, 90: 46000 },
};

// Precio presencial con transferencia/efectivo.
const PRECIO_PRESENCIAL_DESCUENTO: Record<TipoPrecio, Record<number, number>> = {
  individual: { 45: 32000, 90: 41000 },
  pareja: { 50: 36000, 90: 41000 },
};

export interface SessionPriceInput {
  tipo: string;             // 'individual' | 'pareja' (otros → sin precio)
  duracion: number;
  modalidad: string;        // 'presencial' | 'online' | 'domicilio'
  metodo: MetodoPagoDeclarado;
  fechaHora?: string | null; // fecha de la sesión (YYYY-MM-DD o ISO), para la promo online
}

/** Precio de la sesión antes de `tarifa_diferenciada`; null si la combinación no existe. */
export function getSessionPrice({ tipo, duracion, modalidad, metodo, fechaHora }: SessionPriceInput): number | null {
  if (modalidad === 'domicilio') {
    return tipo === 'pareja' && duracion === 90 ? PRECIO_DOMICILIO : null;
  }
  if (tipo !== 'individual' && tipo !== 'pareja') return null;

  const lista = PRECIO_LISTA[tipo][duracion];
  if (lista === undefined) return null;

  if (modalidad === 'online') {
    return precioListaConPromo(lista, { tipo, duracion, modalidad, fecha: fechaHora });
  }
  return metodo === 'tarjeta' ? lista : PRECIO_PRESENCIAL_DESCUENTO[tipo][duracion];
}

/**
 * Ahorro por pagar con transferencia/efectivo vs. débito/crédito (0 si el método
 * no cambia el precio: online, domicilio, o 90 min individual/pareja iguales).
 */
export function getAhorroTransferencia(input: Omit<SessionPriceInput, 'metodo'>): number {
  const conTarjeta = getSessionPrice({ ...input, metodo: 'tarjeta' });
  const conTransferencia = getSessionPrice({ ...input, metodo: 'transferencia' });
  if (conTarjeta === null || conTransferencia === null) return 0;
  return Math.max(0, conTarjeta - conTransferencia);
}

/** Etiqueta corta para UI/calendario. */
export function labelMetodoPago(metodo: string | null | undefined): string {
  return metodo === 'tarjeta' ? 'Débito/Crédito' : 'Transferencia / Efectivo';
}
